export type SeriesDiscussionTarget = {
    discussionPublicId?: string;
    discussionSlugOrPublicId?: string;
    title?: string;
};

export type SeriesReadingState = {
    seriesPublicId?: string;
    seriesSlug?: string;
    seriesTitle?: string;
    firstDiscussion?: SeriesDiscussionTarget | null;
    targetDiscussion?: SeriesDiscussionTarget | null;
    currentPosition?: number | null;
    totalCount?: number | null;
    resumable?: boolean | null;
    completed?: boolean | null;
};

export type SeriesReadingContext = {
    seriesPublicId?: string;
    seriesSlug?: string;
    seriesTitle?: string;
    currentPosition?: number | null;
    totalCount?: number | null;
    completed?: boolean | null;
    previousDiscussion?: SeriesDiscussionTarget | null;
    nextDiscussion?: SeriesDiscussionTarget | null;
};

function trimToUndefined(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function normalizeTarget(value: unknown): SeriesDiscussionTarget | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const record = value as Record<string, unknown>;
    const discussionPublicId = trimToUndefined(record.discussionPublicId);
    const discussionSlugOrPublicId = trimToUndefined(record.discussionSlugOrPublicId);
    const title = trimToUndefined(record.title);
    if (!discussionPublicId && !discussionSlugOrPublicId) {
        return undefined;
    }

    return {
        discussionPublicId,
        discussionSlugOrPublicId,
        title,
    };
}

function normalizeNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function normalizeBoolean(value: unknown): boolean | undefined {
    return typeof value === "boolean" ? value : undefined;
}

export function normalizeSeriesReadingState(value: unknown): SeriesReadingState | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const record = value as Record<string, unknown>;
    const seriesPublicId = trimToUndefined(record.seriesPublicId);
    const firstDiscussion = normalizeTarget(record.firstDiscussion);
    const targetDiscussion = normalizeTarget(record.targetDiscussion);
    if (!seriesPublicId || (!firstDiscussion && !targetDiscussion)) {
        return undefined;
    }

    return {
        seriesPublicId,
        seriesSlug: trimToUndefined(record.seriesSlug),
        seriesTitle: trimToUndefined(record.seriesTitle),
        firstDiscussion,
        targetDiscussion,
        currentPosition: normalizeNumber(record.currentPosition),
        totalCount: normalizeNumber(record.totalCount),
        resumable: normalizeBoolean(record.resumable),
        completed: normalizeBoolean(record.completed),
    };
}

export function normalizeSeriesReadingContext(value: unknown): SeriesReadingContext | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const record = value as Record<string, unknown>;
    const seriesPublicId = trimToUndefined(record.seriesPublicId);
    if (!seriesPublicId) {
        return undefined;
    }

    return {
        seriesPublicId,
        seriesSlug: trimToUndefined(record.seriesSlug),
        seriesTitle: trimToUndefined(record.seriesTitle),
        currentPosition: normalizeNumber(record.currentPosition),
        totalCount: normalizeNumber(record.totalCount),
        completed: normalizeBoolean(record.completed),
        previousDiscussion: normalizeTarget(record.previousDiscussion),
        nextDiscussion: normalizeTarget(record.nextDiscussion),
    };
}

export function buildSeriesDiscussionHref(
    discussionSlugOrPublicId: string | undefined,
    options?: {
        contextGroupPublicId?: string;
        contextSeriesPublicId?: string;
    },
): string {
    const safeDiscussionSlugOrPublicId = (discussionSlugOrPublicId ?? "").trim();
    const basePath = `/discussion/${encodeURIComponent(safeDiscussionSlugOrPublicId)}`;
    const searchParams = new URLSearchParams();

    if (options?.contextGroupPublicId?.trim()) {
        searchParams.set("fromGroupPublicId", options.contextGroupPublicId.trim());
    }
    if (options?.contextSeriesPublicId?.trim()) {
        searchParams.set("fromSeriesPublicId", options.contextSeriesPublicId.trim());
    }

    const query = searchParams.toString();
    return query ? `${basePath}?${query}` : basePath;
}
